import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

const SurveyView = ({ survey }) => {
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  const toggleQuestion = (index) => {
    setExpandedQuestion(expandedQuestion === index ? null : index);
  };

  if (!survey) {
    return (
      <div className="text-center text-gray-500 mt-8">
        <p>No survey data available.</p>
      </div>
    );
  }

  const renderQuestionInput = (question) => {
    const options = question.question_options ? question.question_options.split(',').map(option => option.trim()) : [];

    switch (question.question_type) {
      case 'Text':
        return (
          <input 
            type="text" 
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500" 
            placeholder="Enter your answer" 
          />
        );
      case 'Multiple Choice':
        return (
          <div className="space-y-2">
            {options.map((option, optionIndex) => (
              <label key={optionIndex} className="flex items-center">
                <input 
                  type="radio" 
                  name={`question-${question.id}`} 
                  className="mr-2 focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300" 
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'Checkbox':
        return (
          <div className="space-y-2">
            {options.map((option, optionIndex) => (
              <label key={optionIndex} className="flex items-center">
                <input 
                  type="checkbox" 
                  className="mr-2 focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded" 
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'Rating Scale':
        return (
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map((value) => (
              <label key={value} className="flex flex-col items-center">
                <input 
                  type="radio" 
                  name={`question-${question.id}`} 
                  value={value}
                  className="sr-only"
                />
                <span className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full cursor-pointer hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500">
                  {value}
                </span>
              </label>
            ))}
          </div>
        );
      case 'Likert Scale':
        return (
          <div className="space-y-2">
            {options.map((option, optionIndex) => (
              <label key={optionIndex} className="flex items-center">
                <input 
                  type="radio" 
                  name={`question-${question.id}`} 
                  className="mr-2 focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300" 
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'Dropdown':
        return (
          <select 
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select an option</option>
            {options.map((option, optionIndex) => (
              <option key={optionIndex} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'Yes/No':
        return (
          <div className="space-x-4">
            <label className="inline-flex items-center">
              <input type="radio" name={`question-${question.id}`} value="Yes" className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300" />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input type="radio" name={`question-${question.id}`} value="No" className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300" />
              <span className="ml-2">No</span>
            </label>
          </div>
        );
      case 'Numeric':
        return (
          <input 
            type="number" 
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500" 
            placeholder="Enter a number" 
          />
        );
      case 'Date':
        return (
          <input 
            type="date" 
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500" 
          />
        );
      case 'Email':
        return (
          <input 
            type="email" 
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500" 
            placeholder="Enter your email" 
          />
        );
      case 'File Upload':
        return (
          <input 
            type="file" 
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500" 
          />
        );
      default:
        return <p className="text-gray-500">Unsupported question type: {question.question_type}</p>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-semibold text-gray-800">{survey.name || 'Untitled Survey'}</h2>
      <p className="text-gray-600">{survey.short_description || 'No description available.'}</p>
      <div className="bg-white dark:bg-gray-800 dark:shadow-gray-900/20 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Survey Details</h3>
        <p className="text-gray-700 mb-6">{survey.long_description || 'No detailed description available.'}</p>
        <div className="space-y-4">
          {survey.questions && survey.questions.length > 0 ? (
            survey.questions.map((question, index) => (
              <motion.div
                key={question.id || index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                initial={false}
                animate={{ height: expandedQuestion === index ? 'auto' : '48px' }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <button
                  className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-600 transition-colors duration-200"
                  onClick={() => toggleQuestion(index)}
                >
                  <span className="font-medium text-gray-700">Question {index + 1}</span>
                  {expandedQuestion === index ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div className="p-4">
                  <p className="text-gray-800 mb-2">{question.question_text}</p>
                  {renderQuestionInput(question)}
                </div>
              </motion.div>
            ))
          ) : (
            <p className="text-gray-500">No questions available for this survey.</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SurveyView;